import clsx from 'clsx';
import { Maybe, just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { emptyFragment } from '../../../EmptyFragment';
import { T } from '../../../Translations';
import withRouter from '../../LearningCollections/LearningCollection/WithRouter';
import ArrowIcon from './ArrowIcon';
import GroupOptionsReactions from './GroupOptionsStore/GroupOptionsReactions';
import GroupState from './GroupState';
import GroupParticipantsReactions from './GroupParticipantsStore/Reactions';
import GroupParticipantsStore from './GroupParticipantsStore/Store';
import { GroupParticipantsResource } from './GroupParticipantsStore/Types';

interface Props {
  params: { id: string };
}

export const getGroupParticipantsResource = (
  store: GroupParticipantsStore,
): Maybe<GroupParticipantsResource> => {
  switch (store.state.kind) {
    case 'ready':
      return just(store.state.resource);
    case 'loading':
    case 'updating-query':
    case 'updating-search-parameters':
    case 'sorting':
    case 'waiting':
    case 'error':
    case 'loading-more':
      return nothing();
  }
};

@observer
class Group extends React.Component<Props, {}> {
  private groupParticipantsStore: GroupParticipantsStore = new GroupParticipantsStore(
    Number(this.props.params.id),
  );

  handleToggle = () => {
    this.groupParticipantsStore.modalStore.open();
  };

  render() {
    const groupId = this.props.params.id;

    return (
      <>
        <div className="mt-7">
          <div className="max-w-clientPortalMaxWidth mx-auto w-full overflow-x-hidden">
            <div className="flex flex-col justify-between md:flex-row lg:px-12">
              <div className="flex items-center justify-between w-full">
                <h2 className="text-2xl font-semibold text-gray-900 items-center flex">
                  <NavLink to="/people/groups">
                    <div className="mr-4 cursor-pointer text-teal-600">
                      <T kind="Groups" />
                    </div>
                  </NavLink>
                  <ArrowIcon className="mr-4" />
                  {getGroupParticipantsResource(this.groupParticipantsStore)
                    .map((groupParticipantsResource) => (
                      <div>{groupParticipantsResource.payload.name}</div>
                    ))
                    .getOrElse(emptyFragment)}
                </h2>
                <div className="flex gap-3">
                  <NavLink
                    to={`/people/groups/${groupId}/edit`}
                    state={{ returnUrl: `/people/groups/${groupId}` }}
                  >
                    <div
                      className={clsx(
                        'cursor-pointer rounded-md border border-solid font-semibold',
                        'border-teal-600 bg-white px-4 py-2 text-sm text-teal-600 w-36',
                        'text-center hover:bg-teal-50 transition-colors',
                      )}
                      data-intercom-target="edit-group-button"
                    >
                      <T kind="Edit Group" />
                    </div>
                  </NavLink>
                  <div
                    id="add-participant-to-group-button"
                    className={clsx(
                      'rounded-md cursor-pointer bg-teal-600 text-white px-3 py-2 text-sm w-36',
                      'text-center font-normal hover:bg-teal-700 transition-colors',
                    )}
                    onClick={this.handleToggle}
                  >
                    <span>+ </span>
                    <T kind="Add Participant" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-clientPortalMaxWidth mx-auto">
          <GroupState
            groupParticipantsStore={this.groupParticipantsStore}
            modalStore={this.groupParticipantsStore.modalStore}
          />
        </div>
        <GroupParticipantsReactions store={this.groupParticipantsStore} fireImmediately />
        <GroupOptionsReactions store={this.groupParticipantsStore.optionsStore} fireImmediately />
      </>
    );
  }
}

export default observer(withRouter(Group));
